// CRM definitions include historical connections such as parked Brivity.
// `appKeys` are Rollout connector keys from Rollout's connector catalog
// (GET https://universal.rollout.com/api/connectors). The Connect popup renders
// only OFFERED_ROLLOUT_CRM_IDS. Helper text follows Rollout's end-user guides
// (https://rollout.com/docs/end-user-guides/crm/<crm>).
//
// To offer another Rollout CRM, add its definition and offered ID here, plus
// an option in CRM_OPTIONS (onboarding.schema.ts) with the same id.

export interface RolloutCrm {
  label: string;
  appKeys: readonly string[];
  /** Button copy before the CRM is connected. */
  connectLabel: string;
  /** Where to find the key / what signing in needs. */
  helperText: string;
}

export const ROLLOUT_CRMS = {
  fub: {
    label: 'Follow Up Boss',
    appKeys: ['follow-up-boss'],
    connectLabel: 'Connect Follow Up Boss',
    helperText:
      "You'll need a Follow Up Boss API key. In Follow Up Boss, go to Admin → Integrations → API Keys & Lead Email and create a key.",
  },
  cinc: {
    label: 'CINC',
    // `cinc-pro` is kept for orgs connected before CINC moved to a single connector.
    appKeys: ['cinc', 'cinc-pro'],
    connectLabel: 'Connect CINC',
    helperText: 'Sign in with your CINC site address, username and password. No API key is needed.',
  },
  brivity: {
    label: 'Brivity',
    appKeys: ['brivity'],
    connectLabel: 'Connect Brivity',
    helperText: "You'll need your Brivity API key from your Brivity account.",
  },
  lofty: {
    label: 'Lofty',
    appKeys: ['lofty'],
    connectLabel: 'Connect Lofty',
    helperText: "You'll need your Lofty API key from your Lofty account.",
  },
  boldtrail: {
    label: 'BoldTrail (formerly kvCORE)',
    // Rollout's "BoldTrail API Key" connector; `boldtrail-oauth` (sign in) is kept
    // so orgs connected that way still show as connected.
    appKeys: ['boldtrail', 'boldtrail-oauth'],
    connectLabel: 'Connect BoldTrail',
    helperText: "You'll need your BoldTrail API token from your BoldTrail account.",
  },
  sierra_interactive: {
    label: 'Sierra Interactive',
    appKeys: ['sierra-interactive'],
    connectLabel: 'Connect Sierra Interactive',
    helperText:
      "You'll need your Sierra Interactive API key. In Sierra Interactive, go to Integrations → Sierra Interactive and copy the API key.",
  },
  sure_send: {
    label: 'Sure Send',
    appKeys: ['sure-send'],
    connectLabel: 'Connect Sure Send',
    helperText:
      "You'll need a Sure Send API token. In Sure Send, go to Settings → Integrations & API → API Tokens, select New Token (Team or User scope) and copy it.",
  },
  salesforce: {
    label: 'Salesforce',
    appKeys: ['salesforce'],
    connectLabel: 'Sign in with Salesforce',
    helperText:
      'Sign in as the Salesforce user who should authorize the connection. No API key or security token is needed.',
  },
} as const satisfies Record<string, RolloutCrm>;

export type RolloutCrmId = keyof typeof ROLLOUT_CRMS;

export const isRolloutCrm = (crmType: string | null | undefined): crmType is RolloutCrmId =>
  Boolean(crmType) && Object.prototype.hasOwnProperty.call(ROLLOUT_CRMS, crmType as string);

/**
 * CRMs onboarding connects through Rollout. Brivity's Rollout connector isn't
 * ready, so onboarding collects its API key and login like a standard CRM
 * (it stays in ROLLOUT_CRMS for orgs already connected through Rollout).
 */
export const connectsThroughRollout = (
  crmType: string | null | undefined
): crmType is RolloutCrmId => isRolloutCrm(crmType) && crmType !== 'brivity';

/**
 * CRMs connected through Speculo's own Connect form instead of Rollout: the
 * client enters what Rollout would ask for, and the team finishes the
 * connection from the saved details. Orgs already connected through Rollout
 * keep that connection.
 */
export const MANUAL_CONNECT_CRMS = {
  cinc: {
    icon: '/crm-icons/cinc.svg',
    fields: ['site_url', 'username', 'password'],
    helperText: 'Enter your CINC site address and the login for your CINC account.',
  },
  lofty: {
    icon: '/crm-icons/lofty.svg',
    fields: ['api_key'],
    helperText: ROLLOUT_CRMS.lofty.helperText,
  },
  boldtrail: {
    icon: '/crm-icons/boldtrail.svg',
    fields: ['api_key'],
    labels: { api_key: 'API Token' },
    helperText: ROLLOUT_CRMS.boldtrail.helperText,
  },
  sierra_interactive: {
    icon: '/crm-icons/sierra_interactive.svg',
    fields: ['api_key'],
    helperText: ROLLOUT_CRMS.sierra_interactive.helperText,
  },
} as const satisfies Partial<
  Record<
    RolloutCrmId,
    {
      icon: string;
      fields: readonly ManualCrmField[];
      /** Field labels where the CRM's own wording differs, e.g. BoldTrail's "API Token". */
      labels?: Partial<Record<ManualCrmField, string>>;
      helperText: string;
    }
  >
>;

/** Shown wherever third-party CRM logos appear. */
export const CRM_TRADEMARK_NOTICE =
  'All trademarks and logos are the property of their respective owners and are used for identification purposes only';

export type ManualCrmField = 'site_url' | 'api_key' | 'username' | 'password';
export type ManualConnectCrmId = keyof typeof MANUAL_CONNECT_CRMS;

export const MANUAL_CONNECT_CRM_IDS = Object.keys(MANUAL_CONNECT_CRMS) as ManualConnectCrmId[];

export const isManualConnectCrm = (
  crmType: string | null | undefined
): crmType is ManualConnectCrmId =>
  Boolean(crmType) && Object.prototype.hasOwnProperty.call(MANUAL_CONNECT_CRMS, crmType as string);

/** New connections offered in the Rollout Connect popup; keep parked Brivity in ROLLOUT_CRMS for old entries. */
export const OFFERED_ROLLOUT_CRM_IDS = [
  'fub',
  // 'sure_send',
  // 'salesforce',
] as const satisfies readonly RolloutCrmId[];

export const ALL_ROLLOUT_CRM_APP_KEYS: string[] = OFFERED_ROLLOUT_CRM_IDS.flatMap((crm) => [
  ...ROLLOUT_CRMS[crm].appKeys,
]);
